import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

// ---------- Token storage (Safari ITP fallback) ----------
// Safari blocks 3rd-party / cross-site cookies by default (Intelligent Tracking
// Prevention). Even with SameSite=None;Secure the preview domain causes the
// HTTP-only cookie to be dropped, producing a login → /me 401 redirect loop.
// To work around this we ALSO accept the access token from the JSON body and
// send it as `Authorization: Bearer <token>`. The HTTP-only cookie still
// works for browsers that accept it; the Bearer header is the safety net.
const ACCESS_TOKEN_KEY = "finly_access_token";
const REFRESH_TOKEN_KEY = "finly_refresh_token";

export const tokenStore = {
  getAccess() {
    try {
      return window.localStorage.getItem(ACCESS_TOKEN_KEY) || null;
    } catch (_) {
      return null;
    }
  },
  set({ access_token, refresh_token }) {
    try {
      if (access_token) window.localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
      if (refresh_token) window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    } catch (_) {}
  },
  clear() {
    try {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (_) {}
  },
};

const api = axios.create({
  baseURL: API_URL,
  // NOTE: withCredentials is intentionally false. The preview ingress strips
  // CORS credentials headers on OPTIONS preflight (returns Access-Control-
  // Allow-Origin: * with no allow-credentials), so any request with
  // withCredentials:true gets blocked by the browser as a "Network Error".
  // We rely entirely on the Bearer token attached below.
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token (if present) to every request — works even when the
// cookie was silently dropped by Safari.
api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// On 401, if the request was not part of the auth flow, the session likely
// expired (cookie dropped / JWT expired). Redirect to /login so the user can
// re-authenticate instead of letting the error bubble up as an unhandled
// promise rejection that crashes the React tree.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthCall =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/me") ||
      url.includes("/auth/logout") ||
      url.includes("/auth/refresh");

    if (status === 401 && !isAuthCall && typeof window !== "undefined") {
      // Token is invalid/expired — drop it so we don't keep retrying.
      tokenStore.clear();
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register" && path !== "/") {
        window.location.replace("/login?session=expired");
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
